import type { ExtensionOptions } from '@/components/tiptap-editor/tiptap-editor';
import { SCROLL_HANDLER_EXTENSION_NAME } from '@/components/tiptap-editor/tiptap-extension/scroll-handler-extension/scroll-handler-extension';
import { paneScrollElement, syncScrollPositionsByAverageRatio } from '@/lib/split-view-scroll-sync';
import { getEditorVersionFilter, getZyVersions, normalizeSelectedZyVersionFilter } from '@/lib/zy-tiptap-utils';
import type { TiptapCollabProvider } from '@tiptap-pro/provider';
import type { Extensions } from '@tiptap/core';
import type { EditorProps } from '@tiptap/pm/view';
import { Editor, type JSONContent } from '@tiptap/react';
import { prosemirrorJSONToYDoc } from '@tiptap/y-tiptap';
import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import * as Y from 'yjs';

export type SplitViewEditorEntry = { version: string; editor: Editor };

function paneVersionListsEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const sa = [...a].sort().join('\0');
    const sb = [...b].sort().join('\0');
    return sa === sb;
}

function computePaneVersions(mainEditor: Editor, contentSourceEditor: Editor): string[] {
    const versionsInDoc = getZyVersions(contentSourceEditor);
    const basePaneVersions = versionsInDoc.length > 0 ? versionsInDoc : [];
    const selectedVersions = normalizeSelectedZyVersionFilter(getEditorVersionFilter(mainEditor));
    return selectedVersions === null ? basePaneVersions : basePaneVersions.filter(v => selectedVersions.includes(v.trim().toLowerCase()));
}

function buildPaneEditorEntries(
    paneVersions: string[],
    sharedYDoc: Y.Doc,
    createPaneEditor: (version: string, sharedYDoc: Y.Doc) => Editor,
): SplitViewEditorEntry[] {
    return paneVersions.map(version => ({
        version,
        editor: createPaneEditor(version, sharedYDoc),
    }));
}

export function useSplitView(params: {
    editor: Editor | null;
    activeEditor: Editor | null;
    setActiveEditor: Dispatch<SetStateAction<Editor | null>>;
    useCollaboration: boolean;
    ydoc: Y.Doc | null;
    collabProvider: TiptapCollabProvider | null;
    baseExtensionOptions: ExtensionOptions;
    omitExtensions: string[];
    getExtensions: (
        ydoc?: Y.Doc | null,
        options?: ExtensionOptions,
        omitExtensions?: string[],
        collabProvider?: TiptapCollabProvider | null,
    ) => Extensions;
    getEditorProps: (overrideProps?: Partial<EditorProps>) => EditorProps;
}): {
    isSplitView: boolean;
    splitEditors: SplitViewEditorEntry[];
    isSyncScroll: boolean;
    setIsSyncScroll: Dispatch<SetStateAction<boolean>>;
    handleToggleSyncScroll: () => void;
    handleSyncScrollPositions: () => void;
    handleToggleSplitView: () => boolean;
} {
    const {
        editor,
        activeEditor,
        setActiveEditor,
        useCollaboration,
        ydoc,
        collabProvider,
        baseExtensionOptions,
        omitExtensions,
        getExtensions,
        getEditorProps,
    } = params;

    const [isSplitView, setIsSplitView] = useState(false);
    const [splitEditors, setSplitEditors] = useState<SplitViewEditorEntry[]>([]);
    const [isSyncScroll, setIsSyncScroll] = useState(true);
    const sharedYDocRef = useRef<Y.Doc | null>(null);
    const splitEditorsRef = useRef<SplitViewEditorEntry[]>([]);
    const activeEditorRef = useRef<Editor | null>(null);
    const isSplitViewRef = useRef(isSplitView);
    const useCollaborationRef = useRef(useCollaboration);
    const selectedFilterKeyRef = useRef('');

    useEffect(() => {
        splitEditorsRef.current = splitEditors;
        activeEditorRef.current = activeEditor;
        isSplitViewRef.current = isSplitView;
        useCollaborationRef.current = useCollaboration;
    }, [splitEditors, activeEditor, isSplitView, useCollaboration]);

    const createPaneEditor = useCallback(
        (version: string, sharedYDoc: Y.Doc) => {
            const paneExtOptions: ExtensionOptions = {
                ...baseExtensionOptions,
                zyVersion: {
                    ...baseExtensionOptions.zyVersion,
                    versionFilter: version,
                },
                zySplitViewCaret: { enabled: true },
            };
            return new Editor({
                editable: true,
                editorProps: getEditorProps(),
                extensions: getExtensions(
                    sharedYDoc,
                    paneExtOptions,
                    useCollaboration
                        ? [...omitExtensions, 'collaborationCaret', SCROLL_HANDLER_EXTENSION_NAME]
                        : [...omitExtensions, SCROLL_HANDLER_EXTENSION_NAME],
                    collabProvider,
                ),
            });
        },
        [baseExtensionOptions, omitExtensions, getExtensions, getEditorProps, useCollaboration, collabProvider],
    );

    const installSplitPanes = useCallback(
        (sharedYDoc: Y.Doc, paneVersions: string[], previousEntries: SplitViewEditorEntry[], preserveActivePane: boolean) => {
            if (paneVersions.length === 0) return;

            for (const { editor: ed } of previousEntries) {
                ed.destroy();
            }

            const editors = buildPaneEditorEntries(paneVersions, sharedYDoc, createPaneEditor);
            setSplitEditors(editors);

            let activeEditor = editors[0].editor;
            if (preserveActivePane && previousEntries.length > 0) {
                const prevActiveVersion = previousEntries.find(e => e.editor === activeEditorRef.current)?.version ?? paneVersions[0];
                activeEditor = (editors.find(e => e.version === prevActiveVersion) ?? editors[0]).editor;
            }
            activeEditor.commands.setTextSelection(0);
            setActiveEditor(activeEditor);
        },
        [createPaneEditor, setActiveEditor],
    );

    const teardownSplitView = useCallback(() => {
        const panes = splitEditorsRef.current;
        const currentActive = activeEditorRef.current;
        sharedYDocRef.current = null;
        selectedFilterKeyRef.current = '';
        if (!useCollaborationRef.current && currentActive && editor) {
            editor.commands.setContentWithMeta(currentActive.state.doc.toJSON());
        }
        for (const { editor: ed } of panes) {
            ed.destroy();
        }
        setSplitEditors([]);
        setActiveEditor(editor);
        setIsSplitView(false);
        setIsSyncScroll(true);
    }, [editor, setActiveEditor]);

    const installSplitPanesRef = useRef(installSplitPanes);

    useEffect(() => {
        installSplitPanesRef.current = installSplitPanes;
    }, [installSplitPanes]);

    useEffect(() => {
        if (!editor) return;

        const handleFilterOrDocChange = () => {
            if (!isSplitViewRef.current || !sharedYDocRef.current) return;

            const nextFilterKey = JSON.stringify(normalizeSelectedZyVersionFilter(getEditorVersionFilter(editor)));
            if (nextFilterKey === selectedFilterKeyRef.current) return;
            selectedFilterKeyRef.current = nextFilterKey;

            const previousEntries = splitEditorsRef.current;
            if (previousEntries.length === 0) return;

            const contentSourceEditor = !useCollaborationRef.current ? previousEntries[0].editor : editor;
            const nextPaneVersions = computePaneVersions(editor, contentSourceEditor);
            if (nextPaneVersions.length === 0) {
                // Empty filter: leave split view and keep edits from the active pane.
                teardownSplitView();
                return;
            }

            const prevVersions = previousEntries.map(e => e.version);
            if (paneVersionListsEqual(prevVersions, nextPaneVersions)) return;

            installSplitPanesRef.current(sharedYDocRef.current, nextPaneVersions, previousEntries, true);
        };

        // setVersionFilter dispatches meta-only transactions, so listen to `transaction` not `update`.
        editor.on('transaction', handleFilterOrDocChange);

        return () => {
            editor.off('transaction', handleFilterOrDocChange);
        };
    }, [editor, teardownSplitView]);

    const handleToggleSyncScroll = useCallback(() => {
        setIsSyncScroll(prev => !prev);
    }, []);

    const handleSyncScrollPositions = useCallback(() => {
        const scrollEls = splitEditors.map(({ editor: ed }) => paneScrollElement(ed)).filter((el): el is HTMLElement => el != null);
        syncScrollPositionsByAverageRatio(scrollEls);
    }, [splitEditors]);

    const handleToggleSplitView = useCallback(() => {
        if (isSplitView) {
            teardownSplitView();
            return true;
        }

        if (!editor) return false;

        const paneVersions = computePaneVersions(editor, editor);
        if (paneVersions.length === 0) return false;

        const docJson = editor.state.doc.toJSON() as JSONContent;
        let sharedYDoc: Y.Doc;
        if (useCollaboration && ydoc) {
            sharedYDoc = ydoc;
        } else {
            sharedYDoc = new Y.Doc();
            const tempYdoc = prosemirrorJSONToYDoc(editor.schema, docJson, 'default');
            const update = Y.encodeStateAsUpdate(tempYdoc);
            Y.applyUpdate(sharedYDoc, update);
        }

        sharedYDocRef.current = sharedYDoc;
        selectedFilterKeyRef.current = JSON.stringify(normalizeSelectedZyVersionFilter(getEditorVersionFilter(editor)));
        installSplitPanes(sharedYDoc, paneVersions, [], false);
        setIsSplitView(true);
        return true;
    }, [isSplitView, editor, useCollaboration, ydoc, installSplitPanes, teardownSplitView]);

    return {
        isSplitView,
        splitEditors,
        isSyncScroll,
        setIsSyncScroll,
        handleToggleSyncScroll,
        handleSyncScrollPositions,
        handleToggleSplitView,
    };
}
